import {
	AnalyteStatus,
	AnalyteType,
	IAnalyte,
} from "../../domain/analyte.interface";
import { AnalyteRequest } from "../../services/analyte.service";

/**
 * SIMS-8814 — the analyte editor's draft, payload and rules.
 *
 * Kept out of the component so the decisions can be tested without mounting a screen,
 * the same split `cupDraft.ts` uses.
 */

export interface AnalyteDraft {
	// `useConfigEditor<T extends Record<string, unknown>>` compares drafts structurally, so
	// the shape has to be indexable — the same reason CupDraft and the Payers draft declare
	// this.
	[key: string]: unknown;
	shortname: string;
	scientificName: string;
	shortnameDisplay: string;
	scientificDisplay: string;
	type: AnalyteType;
	status: AnalyteStatus;
}

export const emptyAnalyteDraft = (): AnalyteDraft => ({
	shortname: "",
	scientificName: "",
	shortnameDisplay: "",
	scientificDisplay: "",
	// The design pre-selects a type rather than leaving it unset (mkAnaDraft, markup 1046).
	type: AnalyteType.OTHER_SUBSTANCE,
	status: AnalyteStatus.ACTIVE,
});

/**
 * A DISPLAY EQUAL TO ITS CANONICAL VALUE IS TREATED AS NOT SET.
 *
 * The service resolves the fallback on every read — a blank `shortnameDisplay` comes back
 * as the `shortname` — so the editor never receives the stored empty value. Without this
 * inversion, reopening an analyte pre-fills both display fields, the user can no longer
 * reach the blank state AC4 is about, and the next save writes the canonical value into
 * storage. That is the stale copy AC3 exists to prevent: rename the shortname afterwards
 * and the Display column still shows the old one.
 *
 * `cupDraft.ts` learned this the hard way on the sibling field; the lesson is applied here
 * before it can bite a second time.
 *
 * The one case this loses is a user who deliberately typed the canonical value as the
 * display. It resolves identically on read, so nothing is visibly different.
 */
export const analyteDraftFrom = (analyte: IAnalyte): AnalyteDraft => ({
	shortname: analyte.shortname ?? "",
	scientificName: analyte.scientificName ?? "",
	shortnameDisplay:
		(analyte.shortnameDisplay ?? "") === (analyte.shortname ?? "")
			? ""
			: (analyte.shortnameDisplay ?? ""),
	scientificDisplay:
		(analyte.scientificDisplay ?? "") === (analyte.scientificName ?? "")
			? ""
			: (analyte.scientificDisplay ?? ""),
	type: analyte.type ?? AnalyteType.OTHER_SUBSTANCE,
	status: analyte.status ?? AnalyteStatus.ACTIVE,
});

/**
 * AC1 — shortname and scientific name are the required pair, matching the design's two
 * asterisked fields and its only save validation ("Shortname and scientific name are
 * required.", markup 1368). Type and Status always carry a value.
 */
export const canSaveAnalyte = (draft: AnalyteDraft): boolean =>
	draft.shortname.trim().length > 0 && draft.scientificName.trim().length > 0;

/**
 * AC2 — the shortname the user sees while typing.
 *
 * Applied on change, as the design does (`onACode`, markup 1428). The service upper-cases
 * again on write, because this transform lives in one handler and anything reaching the
 * API another way would be stored as typed.
 */
export const normaliseShortnameInput = (value: string): string =>
	value.toUpperCase();

/**
 * AC4 — what gets sent.
 *
 * Display fields are sent EXACTLY as typed, trimmed, and **empty when blank**. The editor
 * never substitutes the canonical value; the service resolves it at read time.
 */
export const toAnalytePayload = (
	draft: AnalyteDraft,
	accountId: string,
	createdBy: string,
): AnalyteRequest => ({
	accountId,
	// Trimmed, internal runs collapsed, upper-cased — the service's own
	// `normalizeShortname`. Without the collapse, "AM  P" is sent as typed and stored as
	// "AM P", so the value the user confirmed is not the value that persists. The input
	// handler only upper-cases, because trimming or collapsing WHILE TYPING would fight
	// the user mid-word.
	shortname: draft.shortname.trim().replace(/\s+/g, " ").toUpperCase(),
	scientificName: draft.scientificName.trim(),
	shortnameDisplay: draft.shortnameDisplay.trim(),
	scientificDisplay: draft.scientificDisplay.trim(),
	type: draft.type,
	status: draft.status,
	createdBy,
});

/**
 * AC2 — a 409 from the uniqueness index is the shortname being taken, not a generic
 * failure.
 *
 * Reads the UNWRAPPED body first: `analyteService` rejects with the response body, so the
 * axios envelope is already gone by the time this sees it. The nested shape is accepted
 * too. This is the shape lesson `duplicateNameMessage` learned on cups — a unit test that
 * invents the error shape passes while the real one falls through.
 */
export const duplicateShortnameMessage = (error: unknown): string | null => {
	const e = error as {
		statusCode?: number;
		message?: unknown;
		response?: { status?: number; data?: { message?: unknown } };
	};
	const status = e?.statusCode ?? e?.response?.status;
	if (status !== 409) {
		return null;
	}
	// WHICH message, and it is not interchangeable. The service rejects with
	// `err.response?.data || err`, so when the 409 carries no body the RAW AXIOS ERROR
	// arrives — and its `.message` is axios's own "Request failed with status code 409",
	// which would be shown to the user as the reason the save was refused. Presence of
	// `.response` is what distinguishes the two shapes: an unwrapped body has none.
	const message = e?.response ? e.response.data?.message : e?.message;
	return typeof message === "string" && message.trim()
		? message
		: "That shortname is already in use.";
};

/**
 * AC3 — the one sentence that states the blast radius of a rename.
 *
 * The design's copy, verbatim (markup 1450-1452). It is a statement, not a confirmation
 * step: AC3 asks that the user be told, not that they be asked.
 */
export const renameImpactMessage = (carryingCount: number): string =>
	carryingCount === 0
		? "Renaming this analyte affects no cup."
		: `Renaming this analyte changes what providers see on ${carryingCount} ${
				carryingCount === 1 ? "cup" : "cups"
			}.`;
